import { useMemo, useState } from "react";
import { NOTE_CLASS } from "../../lib/ui.js";
import LaunchCard from "../LaunchCard.jsx";
import { corpusFromBodies } from "../../lib/xrayFactoryCorpus.js";
import { capabilityCatalog } from "../../../xray-factory/src/mine/capability.js";
import { clusterCalls } from "../../../xray-factory/src/mine/cluster.js";
import { gateFor } from "../../../xray-factory/src/loop/index.js";
import { discriminatorSupport, probeSupport, programFamily } from "../../../xray-factory/src/judge/index.js";
import { redactPaths } from "../../../xray-factory/src/mine/skeleton.js";
import { draftSpecFromVerdict } from "../../lib/xrayRuntimeLensDraft.js";
import { saveRuntimeLensSpec, listRuntimeLensSpecs } from "../../lib/xrayRuntimeLensStore.js";
import { saveBehaviourArtifact } from "../../lib/behaviourArtifactStore.js";
import MountedLensesSection from "./MountedLensesSection.jsx";
import BehaviourArtifactsSection from "./BehaviourArtifactsSection.jsx";

// X-Ray › Behaviours — the discovery pipeline, run against the window in front of
// you instead of an exported corpus.
//
// The tab is named for what it gives a reader — which behaviours recur here and
// which are worth a reading of their own. The FACTORY is the machinery behind it
// (`xray-factory/`, plus lib/xrayFactoryCorpus + lib/xrayFactoryRun), which is
// why those keep the other name.
//
// The stages render as ONE numbered pipeline, in the order the data flows:
// what was read (corpus) → what the agents were offered → what recurs
// (candidates) → what a judge says is worth building (verdicts) → what is
// reading now (mounted). Each stage's output is the next one's input, and the
// spine down the left edge exists to make that legible — five sibling blocks
// with no stated relationship read as five unrelated reports. ORIENT still sits
// second because the tool definitions in the raw bodies are the cheapest and
// best evidence available: a server author writing "Read-only" has described
// the act in the vocabulary of the system being acted on, for free. Everything
// after it infers, and every inference here has a documented way of failing
// silently.
//
// The judge is the only model call, and it is run-gated like Describe. What the
// deterministic stages show is complete without it — the clusters and the
// offered-never-called gap are readings in their own right.
//
// What this panel must never do is imply coverage. Each section states what it
// looked at and what it dropped; a stage that cannot pass its gate renders the
// gate's own reasons rather than an empty list, because an empty list and a
// refused measurement look identical and the empty one reads as a clean answer.

// Deliberately below the package default of 3. A scope window is much smaller
// than an exported corpus, and the gate — not this constant — is what decides
// whether the result is believable.
const MIN_INSTANCES = 2;
const CLUSTER_ROWS = 24;
const CATALOG_ROWS = 40;

const CARD = "rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)]";

// One stop on the pipeline spine. The numbered node and the rail segment are
// what turn five stacked sections into one process: each stage's output is the
// next one's input, and the line says so without a paragraph. The segment hangs
// below its own node and stops at the next one (the -18px matches the panel's
// stage gap), so the last stage ends the line instead of pointing at nothing.
function Stage({ n, last = false, children }) {
  return (
    <div className="relative min-w-0 pl-[26px]">
      {last ? null : (
        <span
          aria-hidden
          className="absolute bottom-[-18px] left-[7px] top-[18px] w-px"
          style={{ backgroundColor: "var(--line-soft)" }}
        />
      )}
      <span
        aria-hidden
        className="absolute left-0 top-0 flex h-[15px] w-[15px] items-center justify-center rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] font-mono text-[8px] leading-none text-[color:var(--muted)]"
      >
        {n}
      </span>
      {children}
    </div>
  );
}

function Section({ title, note, children, right = null }) {
  return (
    <section className="grid min-w-0 gap-[8px]">
      <div className="flex min-w-0 items-baseline justify-between gap-[10px]">
        <h3 className="m-0 text-[11px] font-medium uppercase leading-[1.1] tracking-[0.06em] text-[color:var(--eyebrow)]">
          {title}
        </h3>
        {right}
      </div>
      {note ? <span className={NOTE_CLASS}>{note}</span> : null}
      {children}
    </section>
  );
}

// A gate's refusal is the product, not an error state. `gateFor` returns what to
// do next rather than only what is wrong, so the reasons render as instructions.
function GateNote({ gate }) {
  if (!gate || gate.ok) return null;
  return (
    <div className={`${CARD} grid gap-[4px] px-[10px] py-[8px]`} style={{ borderColor: "var(--line-strong)" }}>
      <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted)]">
        Stopped at {gate.stage}
      </span>
      {gate.reasons.map((r, i) => (
        <span key={i} className="text-[11px] leading-[1.4] text-[color:var(--ink)]">{r}</span>
      ))}
    </div>
  );
}

// Names one list inside a section. Exists because Offered stacks two lists of
// DIFFERENT units — servers, then tools — and unlabelled they read as a single
// list that changes shape halfway down.
function SubHead({ label, note = null, right = null }) {
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-[10px]">
      <span className="min-w-0 truncate text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted)]">
        {label}
        {note ? <span className="text-[color:var(--muted-soft)]"> · {note}</span> : null}
      </span>
      {right}
    </div>
  );
}

// Matches the run-again affordance the sibling run-gated panels use (Describe's
// FilterChip, WorkflowsPanel's). A model call that failed or that ran against a
// window you have since scrubbed away needs a way back without a scope change.
function Chip({ label, onClick, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="shrink-0 rounded-[2px] border border-[color:var(--line)] px-[8px] py-[2px] text-[10px] uppercase leading-[1.2] tracking-[0.04em] text-[color:var(--muted)] transition-colors hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:cursor-default disabled:opacity-40 disabled:hover:border-[color:var(--line)] disabled:hover:text-[color:var(--muted)] cursor-pointer"
    >
      {label}
    </button>
  );
}

function Bar({ value, max, tone = "var(--green)" }) {
  const pct = max > 0 ? Math.max(1, Math.round((100 * value) / max)) : 0;
  return (
    <span className="block h-[3px] w-full" style={{ backgroundColor: "var(--line-soft)", opacity: 0.5 }}>
      <span className="block h-full" style={{ width: `${pct}%`, backgroundColor: tone, opacity: 0.75 }} />
    </span>
  );
}

// ── Corpus. The selection IS a claim about what was mined, so it renders
// before anything derived from it. Compact by default — a skyline of session
// depths says "where the corpus came from" in one glance; the per-session rows
// are a drill-in, because twelve identical bars with hashes under them justify
// no attention until something looks wrong.
function CorpusSection({ selection, totals }) {
  const [open, setOpen] = useState(false);
  const minutes = selection.span.ms == null ? null : Math.round(selection.span.ms / 60000);
  const maxDepth = selection.chosen[0]?.depth || 1;
  // A window can be thin (little work here) or merely SHORT (plenty of work,
  // four minutes of it). Widening the count does not fix the second — the same
  // cumulative bodies dedupe away — so the two are named separately.
  const short = minutes != null && minutes < 10 && selection.totalSessions > 2;
  return (
    <Section
      title="Corpus"
      note={
        `${selection.chosen.length} deepest of ${selection.totalSessions} session${selection.totalSessions === 1 ? "" : "s"}`
        + ` · ${totals.bodies} bodies · ${totals.shellCalls} shell calls of ${totals.toolUses} tool calls`
        + (minutes != null ? ` · spanning ${minutes} min` : "")
      }
      right={<Chip label={open ? "Hide sessions" : "Sessions"} onClick={() => setOpen((v) => !v)} />}
    >
      {/* One full-width column per session: its depth as the value label, its
          id as the axis, its bar on a shared baseline. When the selection took
          fewer prompts than the session holds in this window (the per-session
          cap), the untaken remainder renders as a dim cap on the same bar — the
          sampling is visible, not implied. */}
      <div className={`${CARD} min-w-0 px-[12px] pb-[7px] pt-[10px]`}>
        <div className="flex min-w-0 items-end gap-[8px] border-b border-[color:var(--line-strong)]">
          {selection.chosen.slice(0, 12).map((c) => {
            const total = Math.max(3, Math.round((56 * c.depth) / maxDepth));
            const taken = Math.max(3, Math.round((56 * c.taken) / maxDepth));
            return (
              <div key={c.sessionId} className="flex min-w-0 flex-1 flex-col items-center gap-[4px]">
                <span className="text-[10px] tabular-nums leading-none text-[color:var(--muted)]">{c.depth}</span>
                <span className="flex w-full flex-col justify-end" style={{ height: 56 }}>
                  {c.taken < c.depth ? (
                    <span
                      className="block w-full"
                      style={{ height: total - taken, backgroundColor: "var(--green)", opacity: 0.25 }}
                    />
                  ) : null}
                  <span
                    className="block w-full"
                    style={{ height: c.taken < c.depth ? taken : total, backgroundColor: "var(--green)", opacity: 0.72 }}
                  />
                </span>
              </div>
            );
          })}
        </div>
        <div className="flex min-w-0 gap-[8px] pt-[4px]">
          {selection.chosen.slice(0, 12).map((c) => (
            <span
              key={c.sessionId}
              className="min-w-0 flex-1 truncate text-center font-mono text-[9px] leading-none text-[color:var(--muted-soft)]"
            >
              {String(c.sessionId).slice(0, 8)}
            </span>
          ))}
        </div>
        <span className={`${NOTE_CLASS} mt-[6px] block`}>
          prompts per session, deepest first
          {selection.chosen.some((c) => c.taken < c.depth) ? " — the dim cap is what the per-session sample left behind" : ""}
        </span>
      </div>
      {open ? (
        <div className={`${CARD} grid min-w-0`}>
          {selection.chosen.slice(0, 12).map((c) => (
            <div
              key={c.sessionId}
              className="grid min-w-0 gap-[4px] border-t border-[color:var(--line-soft)] px-[10px] py-[6px] first:border-t-0"
            >
              <span className="flex min-w-0 items-baseline justify-between gap-[10px]">
                <span className="min-w-0 truncate font-mono text-[11px] text-[color:var(--ink)]">
                  {String(c.sessionId).slice(0, 12)}
                </span>
                <span className="shrink-0 text-[10px] tabular-nums text-[color:var(--muted)]">
                  {c.taken}{c.taken < c.depth ? ` of ${c.depth}` : ""} prompt{c.depth === 1 ? "" : "s"}
                </span>
              </span>
              <Bar value={c.depth} max={maxDepth} />
            </div>
          ))}
        </div>
      ) : null}
      <span className={NOTE_CLASS}>
        Depth counts prompts within this window. Sessions with little overlap appear shallow.
        {selection.shallow ? ` ${selection.shallow} single-prompt session${selection.shallow === 1 ? "" : "s"} dropped out of the ranking.` : ""}
        {short ? " This window spans less than ten minutes. Widen the time range first." : ""}
      </span>
    </Section>
  );
}

// ── Orient. What the agents were offered, in the words of whoever wrote it.
// Servers stay visible — nine one-line rows, and an idle server is the
// finding. The TOOL list with its shipped descriptions is the wall, so it
// drills in: the headline carries the counts, the list waits to be asked.
function OfferedSection({ catalog }) {
  const [showUncalled, setShowUncalled] = useState(true);
  const [showTools, setShowTools] = useState(false);
  const tools = catalog.tools;
  const uncalled = catalog.unused();
  const servers = catalog.servers();
  const maxCalls = Math.max(1, ...tools.map((t) => t.calls));

  if (!tools.length) {
    // NOT an absence claim. The catalog is captured — `canonicalize` in the
    // capture-parser guest decomposes `tools` into a ToolCatalog section of
    // content-addressed items, and the `api_request` reassembled for the
    // analytics row restores `messages` without re-inlining that section. So
    // the honest statement is "not reachable from here", and saying "none were
    // offered" would be the absence-claim defect: attributing a gap to the
    // shape of what was read rather than to where the data went.
    return (
      <Section title="Offered">
        <span className={NOTE_CLASS}>
          Tool offers cannot be determined from this window.
        </span>
      </Section>
    );
  }

  // Two different units on this screen, so they get named. A SERVER is the
  // thing someone decides to connect or drop; a TOOL is the thing an agent
  // reaches for. Stacked unlabelled they read as one list that inexplicably
  // changes shape halfway down — and the filter below applies only to the
  // tools, which is invisible when neither list says what it is.
  const shownTools = showUncalled ? uncalled : tools;
  const rows = shownTools.slice(0, CATALOG_ROWS);
  const idleServers = servers.filter((s) => !s.calls).length;
  const direct = tools.filter((t) => !t.server).length;

  return (
    <Section
      title="Offered"
      note={
        `${tools.length} tool${tools.length === 1 ? "" : "s"} offered · ${uncalled.length} never called`
      }
    >
      {servers.length ? (
        <div className="grid min-w-0 gap-[4px]">
          <SubHead
            label={`${servers.length} MCP server${servers.length === 1 ? "" : "s"}`}
            note={idleServers
              ? `${idleServers} with no calls`
              : null}
          />
          <div className={`${CARD} grid min-w-0`}>
            {servers.map((s) => (
              <div
                key={s.server}
                className="flex min-w-0 items-baseline justify-between gap-[10px] border-t border-[color:var(--line-soft)] px-[10px] py-[5px] first:border-t-0"
              >
                <span className="min-w-0 truncate font-mono text-[11px] text-[color:var(--ink)]">{s.server}</span>
                <span
                  className="shrink-0 text-[10px] tabular-nums"
                  style={{ color: s.used ? "var(--muted)" : "var(--red,#c0392b)" }}
                >
                  {s.used}/{s.tools} tools called · {s.calls} call{s.calls === 1 ? "" : "s"}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="grid min-w-0 gap-[4px]">
        <SubHead
          label={showUncalled
            ? `${uncalled.length} tool${uncalled.length === 1 ? "" : "s"} never called`
            : `${tools.length} tool${tools.length === 1 ? "" : "s"} offered`}
          note={direct ? `${direct} direct offer${direct === 1 ? "" : "s"}` : null}
          right={
            <span className="flex shrink-0 items-center gap-[6px]">
              {showTools ? (
                // On the TOOLS block, not the section header: it filters this
                // list only, and the server rollup above deliberately keeps
                // showing every server — a server with nothing called is the
                // finding, so hiding the called ones would remove its contrast.
                <Chip
                  label={showUncalled ? "Show all" : "Never called only"}
                  onClick={() => setShowUncalled((v) => !v)}
                />
              ) : null}
              <Chip label={showTools ? "Hide tools" : "Show tools"} onClick={() => setShowTools((v) => !v)} />
            </span>
          }
        />
        {showTools ? (
          <div className={`${CARD} grid min-w-0`}>
            {rows.map((t) => (
              <div
                key={t.name}
                className="grid min-w-0 gap-[3px] border-t border-[color:var(--line-soft)] px-[10px] py-[6px] first:border-t-0"
              >
                <span className="flex min-w-0 items-baseline justify-between gap-[10px]">
                  <span className="min-w-0 truncate font-mono text-[11px] text-[color:var(--ink)]">{t.name}</span>
                  <span className="shrink-0 text-[10px] tabular-nums text-[color:var(--muted)]">
                    {t.calls ? `${t.calls} call${t.calls === 1 ? "" : "s"} · ${t.calledIn}/${t.offeredIn} sess` : `offered in ${t.offeredIn}`}
                  </span>
                </span>
                {t.description ? (
                  <span className="line-clamp-2 text-[11px] leading-[1.35] text-[color:var(--muted)]">
                    {t.description}
                  </span>
                ) : (
                  <span className={NOTE_CLASS}>No description shipped with this definition.</span>
                )}
                {t.calls ? <Bar value={t.calls} max={maxCalls} /> : null}
              </div>
            ))}
            {shownTools.length > rows.length ? (
              <span className={`${NOTE_CLASS} border-t border-[color:var(--line-soft)] px-[10px] py-[5px]`}>
                +{shownTools.length - rows.length} more not shown
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </Section>
  );
}

const KIND_TONE = {
  behaviour: { borderColor: "var(--green)", color: "var(--ink)", backgroundColor: "var(--green-soft)" },
  scaffolding: { borderColor: "var(--line)", color: "var(--muted)" },
  "already-covered": { borderColor: "var(--line)", color: "var(--muted)" },
  "too-varied": { borderColor: "var(--line)", color: "var(--muted)" },
};

// One recurring shape, expandable onto its evidence. Factored out because the
// family view renders these inside an expanded family rather than as one long
// flat wall.
function ClusterRow({ c, isSel, onSelect, verdict }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(isSel ? null : c.key)}
      className={[
        "grid w-full min-w-0 gap-[4px] border-t border-[color:var(--line-soft)] px-[10px] py-[6px] text-left transition-colors first:border-t-0 cursor-pointer",
        isSel ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--bg)]",
      ].join(" ")}
    >
      <span className="flex min-w-0 items-baseline justify-between gap-[8px]">
        <span className="min-w-0 truncate font-mono text-[11px] leading-[1.35] text-[color:var(--ink)]">
          {c.skeleton}
        </span>
        {verdict ? (
          <span
            className="shrink-0 rounded-[2px] border px-[5px] py-[1px] text-[9px] uppercase tracking-[0.06em]"
            style={KIND_TONE[verdict.kind] || KIND_TONE.scaffolding}
          >
            {verdict.kind === "behaviour" ? `behaviour · ${verdict.name}` : verdict.kind}
          </span>
        ) : null}
      </span>
      {/* `mutates` is tri-state. Absence means classified read-only;
        "consequence unclear" means the verb was not classified, and the
        two must stay distinguishable — collapsing them is the boolean
        that made every candidate claim it. */}
      <span className="text-[10px] tabular-nums leading-[1.3] text-[color:var(--muted)]">
        {c.count} × · {c.sessions} sess · {c.endpoints} ep · conc {c.concentration}
        {c.consequence?.mutates === true ? " · mutates" : ""}
        {c.consequence?.mutates === null ? " · consequence unclear" : ""}
        {c.consequence?.egress ? " · egress" : ""}
        {c.embedded ? ` · embedded ${c.embedded}` : ""}
      </span>
      {isSel ? (
        // The evidence, at full width. A row of statistics contains nothing
        // decidable, and reading three raw commands has reversed this
        // pipeline's worst call. The slot lines are the whole cluster's
        // values — the spread the sampled commands below cannot show.
        <span className="mt-[4px] grid gap-[3px] border-t border-[color:var(--line-soft)] pt-[5px]">
          {(c.slots || []).slice(0, 4).map((s) => (
            <span key={s.ph} className="min-w-0 truncate font-mono text-[10px] leading-[1.4] text-[color:var(--muted)]">
              <span className="text-[color:var(--ink)]">{s.ph}</span>
              {` ${s.distinct}${s.truncated ? "+" : ""} distinct over ${s.total}`}
              {s.top?.length ? ` · ${s.top.map((t) => `${redactPaths(t.value)} ×${t.n}`).join(" · ")}` : ""}
            </span>
          ))}
          {(c.instances || []).slice(0, 6).map((inst, i) => (
            <span
              key={i}
              className="whitespace-pre-wrap break-all font-mono text-[10.5px] leading-[1.4] text-[color:var(--muted)]"
            >
              {redactPaths(inst.command)}
            </span>
          ))}
        </span>
      ) : null}
    </button>
  );
}

const FAMILY_ROWS = 14;

// ── Discover. Grouped by PROGRAM FAMILY, because that is where patterns come
// from: 146 shapes read as noise, but "sed carries a third of the recurring
// work in ten sessions" is a scannable claim, and the shapes (then their raw
// evidence) are two drills down for the reader who decides to care.
//
// `verdictByKey` is the back-link from the NEXT stage: once the judge has run,
// each family and each shape wears its verdicts, so the two lists visibly
// describe the same things instead of reading as unrelated reports.
function CandidatesSection({ mining, gate, selected, onSelect, verdictByKey = null }) {
  const [openFamily, setOpenFamily] = useState(null);
  const families = useMemo(() => {
    const byFamily = new Map();
    for (const c of mining.clusters) {
      const f = programFamily(c);
      let rec = byFamily.get(f);
      if (!rec) { rec = { family: f, clusters: [], calls: 0, sessions: 0 }; byFamily.set(f, rec); }
      rec.clusters.push(c);
      rec.calls += c.count || 0;
      rec.sessions = Math.max(rec.sessions, c.sessions || 0);
    }
    return [...byFamily.values()].sort((a, b) => b.calls - a.calls);
  }, [mining.clusters]);
  const shown = families.slice(0, FAMILY_ROWS);
  const maxCalls = Math.max(1, ...families.map((f) => f.calls));

  const verdictRollup = (f) => {
    if (!verdictByKey) return null;
    const counts = {};
    for (const c of f.clusters) {
      const v = verdictByKey.get(c.key);
      if (v) counts[v.kind] = (counts[v.kind] || 0) + 1;
    }
    return Object.keys(counts).length ? counts : null;
  };

  return (
    <Section
      title="Candidates"
      note={
        `${mining.clusters.length} recurring shape${mining.clusters.length === 1 ? "" : "s"} in `
        + `${families.length} program famil${families.length === 1 ? "y" : "ies"} · `
        + `${mining.totals.calls} calls · ${mining.tailCount} one-offs. Most agent shell work occurs once; this view highlights recurring work.`
      }
    >
      <GateNote gate={gate} />
      {shown.length ? (
        <div className={`${CARD} grid min-w-0`}>
          {shown.map((f) => {
            const isOpen = openFamily === f.family;
            const rollup = verdictRollup(f);
            return (
              <div key={f.family} className="grid min-w-0 border-t border-[color:var(--line-soft)] first:border-t-0">
                <button
                  type="button"
                  onClick={() => setOpenFamily(isOpen ? null : f.family)}
                  className={[
                    "grid w-full min-w-0 gap-[4px] px-[10px] py-[6px] text-left transition-colors cursor-pointer",
                    isOpen ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--bg)]",
                  ].join(" ")}
                >
                  <span className="flex min-w-0 items-baseline justify-between gap-[8px]">
                    <span className="min-w-0 truncate font-mono text-[11px] leading-[1.35] text-[color:var(--ink)]">
                      {f.family}
                    </span>
                    <span className="flex shrink-0 items-baseline gap-[6px]">
                      {rollup?.behaviour ? (
                        <span
                          className="rounded-[2px] border px-[5px] py-[1px] text-[9px] uppercase tracking-[0.06em]"
                          style={KIND_TONE.behaviour}
                        >
                          {rollup.behaviour} behaviour{rollup.behaviour === 1 ? "" : "s"}
                        </span>
                      ) : null}
                      {rollup && !rollup.behaviour ? (
                        <span
                          className="rounded-[2px] border px-[5px] py-[1px] text-[9px] uppercase tracking-[0.06em]"
                          style={KIND_TONE.scaffolding}
                        >
                          judged
                        </span>
                      ) : null}
                      <span className="text-[10px] tabular-nums text-[color:var(--muted)]">
                        {f.clusters.length} shape{f.clusters.length === 1 ? "" : "s"} · {f.calls} calls · {f.sessions} sess
                      </span>
                    </span>
                  </span>
                  <Bar value={f.calls} max={maxCalls} />
                </button>
                {isOpen ? (
                  <div className="grid min-w-0 border-t border-[color:var(--line-soft)] pl-[10px]">
                    {f.clusters.slice(0, CLUSTER_ROWS).map((c) => (
                      <ClusterRow
                        key={c.key}
                        c={c}
                        isSel={selected === c.key}
                        onSelect={onSelect}
                        verdict={verdictByKey?.get(c.key) || null}
                      />
                    ))}
                    {f.clusters.length > CLUSTER_ROWS ? (
                      <span className={`${NOTE_CLASS} border-t border-[color:var(--line-soft)] px-[10px] py-[5px]`}>
                        +{f.clusters.length - CLUSTER_ROWS} lighter shapes not shown
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
          {families.length > shown.length ? (
            <span className={`${NOTE_CLASS} border-t border-[color:var(--line-soft)] px-[10px] py-[5px]`}>
              +{families.length - shown.length} lighter families not shown
            </span>
          ) : null}
        </div>
      ) : null}
    </Section>
  );
}

// The mount affordance on a judged behaviour. This is where the loop closes: a
// candidate the judge named becomes a reading you can watch, without a deploy.
//
// Only for `behaviour` verdicts, and only when a spec can actually be drafted — a
// cluster whose commands resolve to no program has no door, and mounting a lens
// that matches nothing reads as "found nothing" rather than "never matched".
function MountAction({ verdict, cluster, onMount, onRefine, state }) {
  const draft = useMemo(
    () => (verdict.kind === "behaviour" && cluster ? draftSpecFromVerdict(verdict, cluster) : null),
    [verdict, cluster],
  );
  if (!draft) return null;

  if (state?.status === "mounted" || state?.status === "refining" || state?.status === "refined") {
    return (
      <div className="grid gap-[4px] border-t border-[color:var(--line-soft)] pt-[6px]">
        <span className="text-[10px] uppercase tracking-[0.06em]" style={{ color: "var(--green)" }}>
          mounted as {state.id}
        </span>
        {/* Live: the catalog is reactive, so the tab strip and the per-trace feed
            pick the lens up on this render. Watching residual move the moment a
            lens mounts is the whole point of the tier. */}
        <span className={NOTE_CLASS}>Now available in the per-trace feed.</span>

        {/* A draft binds the statement and the program, which groups every row under
            one program and shows a reader nothing they could act on. This is the step
            that makes it a reading: one model call over the raw instances to locate
            the fields the judge already named. */}
        {state.status === "refining" ? (
          <span className={`${NOTE_CLASS} animate-pulse`}>Reading the instances to locate {state.wanted?.join(", ")}…</span>
        ) : state.status === "refined" ? (
          <div className="grid gap-[3px]">
            <span className="text-[10px] uppercase tracking-[0.06em]" style={{ color: "var(--green)" }}>
              refined · grouped by {state.groupBy}
            </span>
            <span className={NOTE_CLASS}>{Object.keys(state.fields).join(", ")}</span>
            {state.unlocatable?.length ? (
              <span className={NOTE_CLASS}>
                Could not locate: {state.unlocatable.join(", ")}.
              </span>
            ) : null}
            {state.silentlyDropped?.length ? (
              <span className={NOTE_CLASS} style={{ color: "var(--red,#c0392b)" }}>
                Dropped without saying why: {state.silentlyDropped.join(", ")}
              </span>
            ) : null}
            {state.notes ? <span className={NOTE_CLASS}>{state.notes}</span> : null}
          </div>
        ) : state.refineProblems?.length ? (
          <div className="grid gap-[3px]">
            {state.refineProblems.map((p, i) => (
              <span key={i} className="text-[10px] leading-[1.4]" style={{ color: "var(--red,#c0392b)" }}>{p}</span>
            ))}
            <Chip label="Refine again" onClick={() => onRefine(state.spec, cluster, state.wanted)} />
          </div>
        ) : (
          <span className="flex flex-wrap items-center gap-[8px]">
            <Chip label="Refine fields" onClick={() => onRefine(state.spec, cluster, state.wanted)} />
            <span className={NOTE_CLASS}>locate {state.wanted?.length ? state.wanted.join(", ") : "the fields"} in the raw instances</span>
          </span>
        )}
      </div>
    );
  }
  if (state?.status === "error") {
    return (
      <div className="grid gap-[4px] border-t border-[color:var(--line-soft)] pt-[6px]">
        {state.problems.map((p, i) => (
          <span key={i} className="text-[10px] leading-[1.4]" style={{ color: "var(--red,#c0392b)" }}>{p}</span>
        ))}
        <span><Chip label="Try again" onClick={() => onMount(draft)} /></span>
      </div>
    );
  }
  return (
    <div className="grid gap-[5px] border-t border-[color:var(--line-soft)] pt-[6px]">
      {draft.spec ? (
        <>
          <span className="flex flex-wrap items-center gap-[8px]">
            <Chip label="Mount as a lens" onClick={() => onMount(draft)} />
            {/* The SHAPE it will claim, so a reader can see the door is exact
                rather than a program list that would match anything running it. */}
            <span className="min-w-0 truncate font-mono text-[10px] text-[color:var(--muted)]">
              {draft.spec.match.shell?.skeleton || (draft.spec.match.tools || []).join(", ")}
            </span>
          </span>
          {/* The notes are the honest part: what the draft binds, what the judge
              asked for that it could not, and why. A reader who mounts without
              seeing them discovers the gap as a missing column. */}
          {draft.notes.map((n, i) => <span key={i} className={NOTE_CLASS}>{n}</span>)}
        </>
      ) : (
        draft.notes.map((n, i) => <span key={i} className={NOTE_CLASS}>{n}</span>)
      )}
    </div>
  );
}

function VerdictCard({ verdict, cluster, onMount, onRefine, mountState, onDraftArtifact, draftState }) {
  const tab = verdict.axis === "scope-tab";
  const behaviour = verdict.kind === "behaviour";
  // The measured split behind a subset claim: how many of the cluster's kept
  // instances the discriminator actually matches. Shown beside the claim rather
  // than left for the mount to reveal, because "conflict-marker finding · 2 of
  // 24 sampled" and the same name over all 24 are different verdicts.
  const support = useMemo(
    () => (behaviour && cluster && verdict.where ? discriminatorSupport(cluster, verdict.where) : null),
    [behaviour, cluster, verdict.where],
  );
  // Every probe's measured support against the same evidence. This is the
  // verdict's semantics decomposed into things that were EXECUTED — the row a
  // reader weighs against the prose above it, and the same probes the mounted
  // lens re-runs on every call it claims.
  const probes = useMemo(
    () => (behaviour && cluster && Array.isArray(verdict.checks)
      ? verdict.checks
        .filter((chk) => chk && chk.name)
        .map((chk) => ({ ...chk, support: probeSupport(cluster, chk) }))
      : []),
    [behaviour, cluster, verdict.checks],
  );
  return (
    <div className={`${CARD} grid min-w-0 gap-[6px] px-[10px] py-[8px]`}>
      <span className="flex min-w-0 flex-wrap items-baseline gap-[8px]">
        <span className="min-w-0 text-[12px] font-medium leading-[1.3] text-[color:var(--ink)]">
          {verdict.name}
        </span>
        <span
          className="shrink-0 rounded-[2px] border px-[5px] py-[1px] text-[9px] uppercase tracking-[0.06em]"
          style={KIND_TONE[verdict.kind] || KIND_TONE.scaffolding}
        >
          {verdict.kind}
        </span>
        {behaviour ? (
          <span
            className="shrink-0 rounded-[2px] border px-[5px] py-[1px] text-[9px] uppercase tracking-[0.06em]"
            style={tab
              ? { borderColor: "var(--green)", color: "var(--ink)" }
              : { borderColor: "var(--line)", color: "var(--muted)" }}
          >
            {tab ? "scope tab" : "trace only"}
          </span>
        ) : null}
        {/* The scope of the claim, at the same weight as the axis: whether the
          name covers everything the shape matches, or only the calls its
          discriminator carves out. */}
        {behaviour && verdict.claimScope ? (
          <span
            className="shrink-0 rounded-[2px] border px-[5px] py-[1px] text-[9px] uppercase tracking-[0.06em]"
            style={verdict.claimScope === "subset"
              ? { borderColor: "var(--line-strong)", color: "var(--ink)" }
              : { borderColor: "var(--line)", color: "var(--muted)" }}
          >
            {verdict.claimScope === "subset" ? "subset" : "whole shape"}
          </span>
        ) : null}
        <span className="shrink-0 text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)]">
          {verdict.confidence}
        </span>
      </span>

      <span className="text-[11.5px] leading-[1.45] text-[color:var(--ink)]">{verdict.summary}</span>

      <span className="grid gap-[2px] text-[11px] leading-[1.4] text-[color:var(--muted)]">
        <span><span className="text-[color:var(--ink)]">Reader:</span> {verdict.consumer}</span>
        <span><span className="text-[color:var(--ink)]">Decision:</span> {verdict.decision}</span>
        {verdict.target ? (
          <span>
            <span className="text-[color:var(--ink)]">Target:</span> {verdict.target}
            {verdict.targetOutlivesSession === false ? " (dies with its session)" : ""}
          </span>
        ) : null}
        {verdict.axisReason ? (
          <span><span className="text-[color:var(--ink)]">Axis:</span> {verdict.axisReason}</span>
        ) : null}
        {verdict.coveredBy ? (
          <span><span className="text-[color:var(--ink)]">Covered by:</span> {verdict.coveredBy}</span>
        ) : null}
        {verdict.where ? (
          <span className="min-w-0 break-all">
            <span className="text-[color:var(--ink)]">Claims:</span> commands matching{" "}
            <span className="font-mono">/{verdict.where}/</span>
            {support ? (
              support.re
                ? ` · ${support.matched} of ${support.total} sampled instances`
                : <span style={{ color: "var(--red,#c0392b)" }}> · pattern does not compile in the lens engine; this verdict cannot be mounted</span>
            ) : ""}
          </span>
        ) : null}
        {probes.length ? (
          <span className="min-w-0">
            <span className="text-[color:var(--ink)]">Checks:</span>{" "}
            {probes.map((p, i) => (
              <span key={p.name}>
                {i ? " · " : ""}
                {p.name} <span className="text-[color:var(--muted-soft)]">({p.on})</span>
                {p.support.re
                  ? <span className="tabular-nums"> {p.support.matched}/{p.support.total}</span>
                  : <span style={{ color: "var(--red,#c0392b)" }}> pattern invalid (js)</span>}
              </span>
            ))}
          </span>
        ) : null}
        {/* The falsifier is what makes a verdict reviewable later against
          whether the lens was ever used, so it renders at the same weight
          as the claim rather than as a footnote. */}
        <span><span className="text-[color:var(--ink)]">Invalid if:</span> {verdict.falsifier}</span>
      </span>

      {(verdict.fields || []).length ? (
        <span className="flex flex-wrap gap-[4px] pt-[2px]">
          {verdict.fields.map((f) => (
            <span
              key={f}
              className="rounded-[2px] border border-[color:var(--line)] px-[5px] py-[1px] font-mono text-[10px] text-[color:var(--muted)]"
            >
              {f}
            </span>
          ))}
        </span>
      ) : null}

      {cluster ? (
        <span className="truncate border-t border-[color:var(--line-soft)] pt-[5px] font-mono text-[10px] text-[color:var(--muted)]">
          {cluster.skeleton}
        </span>
      ) : null}

      {behaviour && cluster ? (
        <ArtifactAction verdict={verdict} onDraft={onDraftArtifact} state={draftState} />
      ) : null}
      <MountAction verdict={verdict} cluster={cluster} onMount={onMount} onRefine={onRefine} state={mountState} />
    </div>
  );
}

// THE build step: a verdict is a judged contract (door, claims, fields), and
// this hands it to an agent to implement as python — seeded with the contract,
// stress-tested against sampled windows, mounted as the lens itself. The
// declarative mount below it is the instant regex-only draft: same contract,
// weaker substrate, no model call — a fallback and a preview, not a peer.
function ArtifactAction({ verdict, onDraft, state }) {
  if (!onDraft) return null;
  if (state?.status === "drafting") {
    return (
      <div className="grid gap-[4px] border-t border-[color:var(--line-soft)] pt-[6px]">
        <span className={`${NOTE_CLASS} animate-pulse`}>
          An agent is implementing this verdict's contract as python and stress-testing it against
          sampled windows…
        </span>
      </div>
    );
  }
  if (state?.status === "drafted") {
    return (
      <div className="grid gap-[4px] border-t border-[color:var(--line-soft)] pt-[6px]">
        <span className="text-[10px] uppercase tracking-[0.06em]" style={{ color: "var(--green)" }}>
          lens built as {state.behaviour}
        </span>
        <span className={NOTE_CLASS}>
          This Python lens runs in an isolated realm. It now reports work in the per-trace feed and can evaluate this window from the Mounted stage.
        </span>
      </div>
    );
  }
  if (state?.status === "error") {
    return (
      <div className="grid gap-[4px] border-t border-[color:var(--line-soft)] pt-[6px]">
        {state.problems.map((p, i) => (
          <span key={i} className="text-[10px] leading-[1.4]" style={{ color: "var(--red,#c0392b)" }}>{p}</span>
        ))}
        <span><Chip label="Draft again" onClick={() => onDraft(verdict)} /></span>
      </div>
    );
  }
  return (
    <div className="grid gap-[4px] border-t border-[color:var(--line-soft)] pt-[6px]">
      <span className="flex flex-wrap items-center gap-[8px]">
        <Chip label="Build lens" onClick={() => onDraft(verdict)} />
        <span className={NOTE_CLASS}>
          An agent implements the door, claims, and fields as Python, tests it against the evidence, and mounts it. Python can parse output, compute fields, and cross-reference the window.
        </span>
      </span>
    </div>
  );
}

function JudgeSection({ judge, gate, canRun, onRun, modelControl, clusterByKey, windowKey, onMount, onRefine, mounts, onDraftArtifact, drafts }) {
  if (judge.status === "running") {
    return (
      <Section title="Verdicts">
        <span className={`${NOTE_CLASS} animate-pulse`}>
          Ranking the candidates against each other…
        </span>
      </Section>
    );
  }
  if (judge.status === "error") {
    // Judge state only resets on a SCOPE change, so without this the user is
    // stranded on a transient model error until they navigate away.
    return (
      <Section title="Verdicts">
        <div className="grid justify-items-start gap-[8px]">
          <span className="break-words font-mono text-[11px] leading-[1.4]" style={{ color: "var(--red,#c0392b)" }}>
            {judge.error}
          </span>
          <div className="flex flex-wrap items-center gap-[8px]">
            {/* `onRun` closes over the candidates that existed when this rendered.
                Scrubbing to a thinner window can leave nothing to rank, and a
                Retry that silently no-ops is worse than one that is visibly
                unavailable. */}
            <Chip label="Retry" onClick={onRun} disabled={!canRun} />
            {modelControl}
            {canRun ? null : (
              <span className={NOTE_CLASS}>No candidates in this window.</span>
            )}
          </div>
        </div>
      </Section>
    );
  }
  if (judge.status !== "done") {
    return (
      <Section title="Verdicts">
        <LaunchCard
          eyebrow="Judge the candidates"
          description={
            "Rank candidates and identify who would use each one, what decision it supports, and"
            + " how to invalidate it. Home paths are redacted before raw commands are sent to the"
            + " selected model. This produces recommendations only."
          }
          controls={modelControl}
          ctaLabel="Judge"
          onLaunch={onRun}
          disabled={!canRun}
          disabledHint={canRun ? null : "No candidates to rank yet."}
        />
      </Section>
    );
  }

  const verdicts = judge.data?.verdicts || [];
  const ranking = judge.data?.ranking || [];
  const order = new Map(ranking.map((k, i) => [k, i]));
  const sorted = [...verdicts].sort((a, b) => (order.get(a.key) ?? 99) - (order.get(b.key) ?? 99));
  const behaviours = sorted.filter((v) => v.kind === "behaviour");
  // The verdicts were ranked against ONE window's candidates and say nothing
  // about another's, so a scrub past the window they were judged for has to show,
  // not silently re-present them as current. Same contract as Describe and
  // Workflows; without it this was the only run-gated stage that could render a
  // previous window's answer as this window's.
  const stale = judge.forKey !== windowKey;

  return (
    <Section
      title="Verdicts"
      note={
        `${behaviours.length} of ${sorted.length} judged a behaviour worth building`
        + (judge.data?.attempts > 1 ? ` · re-asked ${judge.data.attempts - 1}× after a rejected verdict set` : "")
      }
      right={<Chip label={stale ? "Judge current window" : "Re-judge"} onClick={onRun} />}
    >
      {stale ? (
        <span className={NOTE_CLASS}>judged a previous window&apos;s candidates</span>
      ) : null}
      {/* "Nothing was worth building" arrives as the judge gate refusing,
        and its wording already says the outcome is valid — a second note
        here saying the same thing reads as the panel apologising for a
        real answer. The gate IS the empty state. */}
      <GateNote gate={gate} />
      <div className="grid gap-[8px]">
        {sorted.map((v) => (
          <VerdictCard
            key={v.key}
            verdict={v}
            cluster={clusterByKey.get(v.key)}
            onMount={(draft) => onMount(v.key, draft)}
            onRefine={(spec, cl, wanted) => onRefine(v.key, spec, cl, wanted)}
            mountState={mounts[v.key]}
            onDraftArtifact={onDraftArtifact ? () => onDraftArtifact(v.key, v, clusterByKey.get(v.key)) : null}
            draftState={drafts?.[v.key]}
          />
        ))}
      </div>
    </Section>
  );
}

export default function XrayFactoryPanel({
  scan,
  selection,
  judge,
  onRunJudge,
  onRefine = async () => ({ ok: false, problems: ["Field refinement is unavailable."] }),
  onDraftArtifact = null,
  windowKey,
  modelControl = null,
  coveredLenses = [],
}) {
  const [selectedCluster, setSelectedCluster] = useState(null);
  // Per-verdict mount outcome. Kept here rather than in the store's subscribers
  // because it is about THIS card's last action, not about the catalog.
  const [mounts, setMounts] = useState({});
  // Per-verdict artifact drafting outcome — same locality argument as mounts.
  const [drafts, setDrafts] = useState({});

  // Draft, then persist THROUGH the contract: the store re-validates the
  // manifest on save, so acceptance and persistence cannot disagree.
  const draftArtifact = onDraftArtifact
    ? async (verdictKey, verdict, cluster) => {
      setDrafts((d) => ({ ...d, [verdictKey]: { status: "drafting" } }));
      const r = await onDraftArtifact(verdict, cluster);
      if (!r?.ok || !r.artifact) {
        setDrafts((d) => ({ ...d, [verdictKey]: { status: "error", problems: r?.problems || ["the agent returned nothing usable"] } }));
        return;
      }
      // Both tiers derive their id from the same verdict name, and both land in
      // the ONE feed-domain namespace — so a python lens built beside this
      // verdict's declarative mount would silently share its id, and the feed
      // would show two same-named lenses claiming one call. Same re-suffix
      // policy as mount() above; replacing an existing ARTIFACT under the same
      // id stays intentional (a re-draft supersedes its predecessor).
      const taken = new Set([...coveredLenses, ...listRuntimeLensSpecs().map((sp) => sp.id)]);
      let toSave = r.artifact;
      if (taken.has(toSave.behaviour)) {
        const base = toSave.behaviour;
        let id = base;
        for (let n = 2; taken.has(id); n += 1) id = `${base.slice(0, 22)}-${n}`.slice(0, 24);
        toSave = { ...toSave, behaviour: id };
      }
      const saved = saveBehaviourArtifact(null, toSave);
      setDrafts((d) => ({
        ...d,
        [verdictKey]: saved.ok
          ? { status: "drafted", behaviour: toSave.behaviour }
          : { status: "error", problems: saved.problems },
      }));
    }
    : null;

  const refine = async (verdictKey, spec, cluster, wanted) => {
    setMounts((m) => ({ ...m, [verdictKey]: { ...m[verdictKey], status: "refining", wanted } }));
    const r = await onRefine(verdictKey, spec, cluster, wanted);
    if (!r?.ok) {
      setMounts((m) => ({
        ...m,
        [verdictKey]: { ...m[verdictKey], status: "mounted", refineProblems: r?.problems || ["The model returned no usable fields."] },
      }));
      return;
    }
    // Re-save under the SAME id, so the refinement replaces the draft rather than
    // mounting a second lens beside it.
    const saved = saveRuntimeLensSpec(null, r.spec, { reserved: coveredLenses });
    setMounts((m) => ({
      ...m,
      [verdictKey]: saved.ok
        ? {
          ...m[verdictKey],
          status: "refined",
          spec: r.spec,
          fields: r.spec.fields,
          groupBy: r.spec.group.by,
          unlocatable: r.unlocatable,
          silentlyDropped: r.silentlyDropped,
          notes: r.notes,
          refineProblems: null,
        }
        : { ...m[verdictKey], status: "mounted", refineProblems: saved.problems },
    }));
  };

  const mount = (verdictKey, draft) => {
    if (!draft?.spec) return;
    // Re-read the taken ids at mount time, not at draft time: another card in this
    // same verdict set may have mounted since, and two lenses cannot share an id.
    const taken = [...coveredLenses, ...listRuntimeLensSpecs().map((sp) => sp.id)];
    const spec = taken.includes(draft.spec.id)
      ? { ...draft.spec, id: `${draft.spec.id.slice(0, 22)}-2`.slice(0, 24) }
      : draft.spec;
    const { ok, problems } = saveRuntimeLensSpec(null, spec, { reserved: coveredLenses });
    setMounts((m) => ({
      ...m,
      [verdictKey]: ok
        // `spec` and `wanted` ride along so Refine can act without re-drafting —
        // a second draft could pick a different id and mount a duplicate.
        ? { status: "mounted", id: spec.id, spec, wanted: draft.wanted }
        : { status: "error", problems },
    }));
  };

  const derived = useMemo(() => {
    if (scan.status !== "done") return null;
    const { calls, catalogBodies, totals } = corpusFromBodies(scan.bodies);
    const r = clusterCalls(calls, { minInstances: MIN_INSTANCES });
    return {
      totals,
      catalog: capabilityCatalog(catalogBodies),
      mining: {
        depthWeighted: true,
        clusters: r.clusters,
        tailCount: r.tail.length,
        totals: r.totals,
      },
    };
  }, [scan]);

  if (scan.status !== "done") {
    return (
      <div className="grid gap-[8px] p-[12px]" aria-busy="true">
        <span className={`${NOTE_CLASS} animate-pulse`}>
          {scan.total
            ? `Hydrating the deepest sessions ${scan.done}/${scan.total}…`
            : "Selecting the window's deepest sessions…"}
        </span>
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="h-[14px] rounded-[2px] bg-[color:var(--line-soft)]" style={{ opacity: 0.7 - i * 0.18 }} />
        ))}
      </div>
    );
  }

  if (!derived) return null;

  const discoverGate = gateFor("discover", { mining: derived.mining });
  const judgeGate = judge.status === "done"
    ? gateFor("judge", { verdicts: judge.data })
    : null;
  const clusterByKey = new Map(derived.mining.clusters.map((c) => [c.key, c]));
  // The back-link the pipeline reads by: once THIS window's judge has run, each
  // candidate row wears its verdict. A previous window's verdicts stay off the
  // candidates — stamping them there would present stale judgement as current.
  const verdictByKey = judge.status === "done" && judge.forKey === windowKey
    ? new Map((judge.data?.verdicts || []).map((v) => [v.key, v]))
    : null;

  return (
    <div className="grid min-w-0 gap-[18px] px-[12px] py-[10px]">
      <Stage n="01">
        <CorpusSection selection={selection} totals={derived.totals} />
      </Stage>
      <Stage n="02">
        <OfferedSection catalog={derived.catalog} />
      </Stage>
      <Stage n="03">
        <CandidatesSection
          mining={derived.mining}
          gate={discoverGate}
          selected={selectedCluster}
          onSelect={setSelectedCluster}
          verdictByKey={verdictByKey}
        />
      </Stage>
      <Stage n="04">
        <JudgeSection
          judge={judge}
          gate={judgeGate}
          canRun={derived.mining.clusters.length > 0}
          onRun={() => onRunJudge(derived.mining.clusters, coveredLenses)}
          modelControl={modelControl}
          clusterByKey={clusterByKey}
          windowKey={windowKey}
          onMount={mount}
          onRefine={refine}
          mounts={mounts}
          onDraftArtifact={draftArtifact}
          drafts={drafts}
        />
      </Stage>
      <Stage n="05" last>
        <div className="grid min-w-0 gap-[10px]">
          <BehaviourArtifactsSection bodies={scan.bodies} />
          <MountedLensesSection bodies={scan.bodies} />
        </div>
      </Stage>
    </div>
  );
}
